import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

export default function Compounding({ user, onLogout }) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-4xl mx-auto">
        
        <Button 
          variant="ghost" 
          onClick={() => navigate('/learn')} 
          className="mb-6 dark:text-gray-300"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Learn Home
        </Button>

        <Card className="glass p-8 mb-6 dark:bg-gray-800">
          <div className="flex items-center space-x-2 mb-4">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              Beginner
            </span>
            <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="w-4 h-4 mr-1" />
              12 min read
            </span>
          </div>

          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-4">
            📈 The Magic of Compounding
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Discover how compound interest can exponentially grow your wealth over time
          </p>

          <div className="prose dark:prose-invert max-w-none space-y-6 text-gray-700 dark:text-gray-300">
            
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Einstein Called It the 8th Wonder</h2>
              <p className="mb-4">
                Albert Einstein reportedly said, <em>"Compound interest is the eighth wonder of the world. 
                He who understands it, earns it; he who doesn't, pays it."</em>
              </p>
              <p className="mb-4">
                Compounding is when you earn returns not just on your original investment, but also on the returns you've already earned. 
                It's like a snowball rolling downhill - it keeps getting bigger and bigger, faster and faster.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Simple vs Compound Interest</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">❌ Simple Interest</h3>
                  <p className="text-sm mb-2">Interest calculated only on the principal amount.</p>
                  <p className="text-sm font-mono">Year 1: ₹1,00,000 + ₹10,000 = ₹1,10,000</p>
                  <p className="text-sm font-mono">Year 2: ₹1,00,000 + ₹10,000 = ₹1,20,000</p>
                  <p className="text-sm font-mono">Year 3: ₹1,00,000 + ₹10,000 = ₹1,30,000</p>
                  <p className="text-sm font-bold mt-2">After 20 years: ₹3,00,000</p>
                </div>
                
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">✅ Compound Interest</h3>
                  <p className="text-sm mb-2">Interest calculated on principal + accumulated interest.</p>
                  <p className="text-sm font-mono">Year 1: ₹1,00,000 × 1.10 = ₹1,10,000</p>
                  <p className="text-sm font-mono">Year 2: ₹1,10,000 × 1.10 = ₹1,21,000</p>
                  <p className="text-sm font-mono">Year 3: ₹1,21,000 × 1.10 = ₹1,33,100</p>
                  <p className="text-sm font-bold mt-2 text-green-600 dark:text-green-400">After 20 years: ₹6,72,750</p>
                </div>
              </div>

              <p className="text-sm italic bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded">
                <strong>Notice the magic?</strong> Same ₹1 lakh, same 10% rate, but compound interest gives you ₹6.72L vs ₹3L with simple interest. 
                That's 2.24x more money!
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Real Example: SIP of ₹5,000/month</h2>
              <p className="mb-4">
                Let's see how a regular SIP in a mutual fund (like investing in Nifty 50 index) can grow with compounding:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Time Period</th>
                      <th className="p-3 text-right">Amount Invested</th>
                      <th className="p-3 text-right">Value @ 12%</th>
                      <th className="p-3 text-right">Gains</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">5 Years</td>
                      <td className="p-3 text-right">₹3,00,000</td>
                      <td className="p-3 text-right font-bold">₹4,12,400</td>
                      <td className="p-3 text-right text-green-600">₹1,12,400</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">10 Years</td>
                      <td className="p-3 text-right">₹6,00,000</td>
                      <td className="p-3 text-right font-bold">₹11,61,695</td>
                      <td className="p-3 text-right text-green-600">₹5,61,695</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">15 Years</td>
                      <td className="p-3 text-right">₹9,00,000</td>
                      <td className="p-3 text-right font-bold">₹25,03,150</td>
                      <td className="p-3 text-right text-green-600">₹16,03,150</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/20">
                      <td className="p-3">20 Years</td>
                      <td className="p-3 text-right">₹12,00,000</td>
                      <td className="p-3 text-right font-bold text-lg">₹49,95,740</td>
                      <td className="p-3 text-right text-green-600 font-bold">₹37,95,740</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm">
                In 20 years, you invested ₹12 lakhs but ended up with nearly ₹50 lakhs! That extra ₹38 lakhs is the magic of compounding.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">The Power of Starting Early</h2>
              <p className="mb-4">
                Time is your biggest ally in compounding. See what happens when two friends invest differently:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">📱 Rohan (Starts at 25)</h3>
                  <p className="text-sm mb-2">Invests ₹5,000/month for 35 years (till 60)</p>
                  <p className="text-sm">Total invested: <strong>₹21,00,000</strong></p>
                  <p className="text-sm">Value at 60: <strong className="text-blue-600 dark:text-blue-400">₹2.57 Crores</strong></p>
                </div>
                
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">⏰ Priya (Starts at 35)</h3>
                  <p className="text-sm mb-2">Invests ₹10,000/month for 25 years (till 60)</p>
                  <p className="text-sm">Total invested: <strong>₹30,00,000</strong></p>
                  <p className="text-sm">Value at 60: <strong className="text-purple-600 dark:text-purple-400">₹1.89 Crores</strong></p>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border-l-4 border-yellow-500">
                <p className="text-sm">
                  <strong>🤯 Mind-blowing fact:</strong> Rohan invested ₹9 lakhs LESS than Priya but ended up with ₹68 lakhs MORE! 
                  Why? He started 10 years earlier. <strong>Time beats money in compounding.</strong>
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">Warren Buffett's Wealth Curve</h2>
              <p className="mb-4">
                Warren Buffett is worth $100+ billion. But here's the shocking fact: <strong>99% of his wealth was accumulated after age 50.</strong>
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border dark:border-gray-600 mb-4">
                  <thead className="bg-gray-100 dark:bg-gray-700">
                    <tr>
                      <th className="p-3 text-left">Age</th>
                      <th className="p-3 text-right">Net Worth</th>
                      <th className="p-3 text-left">Insight</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">26</td>
                      <td className="p-3 text-right">$140,000</td>
                      <td className="p-3">Started investing early</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">39</td>
                      <td className="p-3 text-right">$25 million</td>
                      <td className="p-3">Getting good returns</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600">
                      <td className="p-3">52</td>
                      <td className="p-3 text-right">$3.8 billion</td>
                      <td className="p-3">Compounding accelerating</td>
                    </tr>
                    <tr className="border-t dark:border-gray-600 bg-green-50 dark:bg-green-900/20">
                      <td className="p-3">94</td>
                      <td className="p-3 text-right font-bold">$100+ billion</td>
                      <td className="p-3">Full power of compounding</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm italic">
                The lesson: Compounding works slowly at first, then explodes exponentially. This is why patience is crucial!
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3">3 Rules to Maximize Compounding</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">1. Start Early, Even with Small Amounts</h3>
                  <p className="text-sm">
                    ₹2,000/month at age 25 beats ₹10,000/month at age 40. Time is more valuable than the amount.
                  </p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">2. Never Withdraw - Let It Compound</h3>
                  <p className="text-sm">
                    Every withdrawal resets the compounding clock. The longer you stay invested without touching, the better.
                  </p>
                </div>

                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h3 className="font-bold text-gray-800 dark:text-gray-100 mb-2">3. Reinvest Dividends and Returns</h3>
                  <p className="text-sm">
                    When your stocks pay dividends or you get returns, reinvest them. Let the returns compound on returns.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold text-xl text-gray-800 dark:text-gray-100 mb-3">💡 Key Takeaways</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Patience wins:</strong> Compounding works slowly at first, then explodes exponentially.</li>
                <li><strong>Start early:</strong> 10 years earlier with less money beats starting late with more money.</li>
                <li><strong>Don't interrupt:</strong> Every withdrawal hurts compounding. Stay invested for decades.</li>
                <li><strong>Consistency matters:</strong> Regular monthly investing (SIP) is better than lump sum timing.</li>
                <li><strong>The Rule of 72:</strong> Divide 72 by your return % to know when your money doubles. At 12%, it doubles every 6 years!</li>
              </ul>
            </div>

          </div>
        </Card>

        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => navigate('/learn/risk-return')}
            className="dark:bg-gray-800 dark:text-gray-100"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: Risk vs Return
          </Button>
          <Button 
            onClick={() => navigate('/learn/financial-statements')}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
          >
            Next: Financial Statements
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
}
